import { useAuth } from "@clerk/clerk-react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BrandMark, Button, buttonVariants, Spinner, Text } from "@/shared/ui";
import { useForkSharedConversation, useSharedConversation } from "../hooks/useSharing";
import { SharedThread } from "./SharedThread";

/** The public marketing site — the shared-page wordmark links here. */
const LANDING_URL = "https://anyaistudio.com";

/**
 * Public, read-only view of a shared conversation (`/share/$token`). Mounted
 * outside the auth shell, so it renders for signed-out visitors. Signed-in
 * visitors can fork the conversation into their account; signed-out visitors get
 * a sign-up CTA. The transcript itself is always readable.
 */
export function SharedConversationPage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token ?? null;
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { data, isPending, isError } = useSharedConversation(token);
  const fork = useForkSharedConversation();

  function continueChat() {
    if (!token) return;
    fork.mutate(token, {
      onSuccess: (conversation) =>
        void navigate({ to: "/chat/$id", params: { id: conversation.id } }),
    });
  }

  const title = data?.conversation.title?.trim() || t("commandPalette.untitled");

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="sticky top-0 z-10 flex items-center justify-between gap-3 bg-canvas/85 px-5 py-3 backdrop-blur-md">
        <a href={LANDING_URL} className="flex items-center gap-2.5 outline-none">
          <BrandMark size={30} />
          <span className="font-sans text-[15px] font-medium text-ink">{t("share.by")}</span>
        </a>

        {isLoaded && data ? (
          isSignedIn ? (
            <Button variant="accent" size="sm" onClick={continueChat} disabled={fork.isPending}>
              {fork.isPending ? t("share.forking") : t("share.fork")}
            </Button>
          ) : (
            <Link to="/sign-in" className={buttonVariants({ variant: "accent", size: "sm" })}>
              {t("share.signInToContinue")}
            </Link>
          )
        ) : null}
      </header>

      <main className="flex-1 overflow-y-auto">
        {isPending ? (
          <div className="grid min-h-[60vh] place-items-center">
            <Spinner size={22} label={t("share.loading")} />
          </div>
        ) : isError || !data ? (
          <div className="mx-auto flex min-h-[60vh] max-w-[460px] flex-col items-center justify-center gap-4 px-6 text-center">
            <BrandMark size={40} />
            <Text variant="h4" as="h1">
              {t("share.notFoundTitle")}
            </Text>
            <Text variant="body" className="text-ink-soft">
              {t("share.notFoundBody")}
            </Text>
            <Link to="/chat" className={buttonVariants({ variant: "ghost", size: "sm" })}>
              {t("share.openConversation")}
            </Link>
          </div>
        ) : (
          <>
            <div className="mx-auto w-full max-w-[760px] px-6 pt-10 max-[880px]:px-4">
              <Text variant="h4" as="h1">
                {title}
              </Text>
              <p className="mt-2 font-sans text-[12px] uppercase tracking-[0.14em] text-ink-faint">
                {t("share.viewerNote")}
              </p>
            </div>

            <SharedThread messages={data.messages} />

            {isLoaded && !isSignedIn ? (
              <div className="bg-canvas-soft">
                <div className="mx-auto flex w-full max-w-[760px] flex-col items-center gap-3 px-6 py-10 text-center max-[880px]:px-4">
                  <Text variant="body" className="text-ink-soft">
                    {t("share.signedOutPitch")}
                  </Text>
                  <Link to="/sign-up" className={buttonVariants({ variant: "ink", size: "md" })}>
                    {t("share.startYourOwn")}
                  </Link>
                </div>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
